import { useEffect, useState } from "react";
import { Settings2, AlertTriangle, ChevronDown } from "lucide-react";
import api from "../lib/api";
import { apiError } from "../lib/auth";
import { useFlash } from "../lib/flash";
import SmsBillingSettings from "./SmsBillingSettings";
import MailSettings from "./MailSettings";

const FIELDS = [
  ["company", "Firmanavn"], ["cvr", "CVR"], ["address", "Adresse"], ["zip_city", "Postnr. og by"],
  ["reg_nr", "Reg.nr.", { inputMode: "numeric", placeholder: "4 cifre" }], ["account_nr", "Kontonr.", { inputMode: "numeric" }],
  ["due_days", "Betalingsfrist (dage)", { type: "number", min: 1, max: 90 }], ["contact_email", "Kontakt-email på fakturaen", { type: "email" }],
  ["next_number", "Næste fakturanr.", { type: "number", min: 1 }], ["vat_pct", "Moms (%)", { type: "number", min: 0, max: 50 }],
];

export default function SellerSettings({ onSaved }) {
  const flash = useFlash();
  const [v, setV] = useState(null);
  const [orig, setOrig] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/owner/seller").then((r) => { setV(r.data); setOrig(r.data); setOpen((o) => o || !r.data.ready); });
  useEffect(() => { load(); }, []);
  if (!v) return null;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    const body = { ...v, due_days: Number(v.due_days), vat_pct: Number(v.vat_pct) };
    body.next_number = Number(v.next_number) !== orig.next_number ? Number(v.next_number) : null;
    try {
      const r = await api.put("/owner/seller", body);
      flash(r.data.flash);
      await load();
      onSaved?.();
    } catch (ex) {
      if (ex.response?.data?.errors) setErrors(ex.response.data.errors);
      else flash(apiError(ex.response?.data?.detail) || ex.message, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel" data-testid="seller-settings">
      <button className="ss-head" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="seller-settings-toggle">
        <Settings2 size={18} /> <b>Fakturaindstillinger</b>
        {!orig.ready && <span className="pill no_show">Bankoplysninger mangler</span>}
        <ChevronDown size={18} style={{ marginLeft: "auto", transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      {!orig.ready && (
        <div className="notice error ss-warn" role="alert" data-testid="seller-missing-bank">
          <AlertTriangle size={16} /> Udfyld reg.nr. og kontonr. – indtil da kan der ikke oprettes fakturaer.
          {orig.pending > 0 && <> {orig.pending} {orig.pending === 1 ? "butik venter" : "butikker venter"} på en faktura.</>}
        </div>
      )}
      {open && (
        <form className="bf-grid ss-grid" onSubmit={save} noValidate>
          {FIELDS.map(([k, label, props]) => (
            <div className="field" key={k}>
              <label htmlFor={`ss-${k}`}>{label}</label>
              <input id={`ss-${k}`} value={v[k] ?? ""} onChange={(e) => setV((o) => ({ ...o, [k]: e.target.value }))}
                aria-invalid={!!errors[k]} data-testid={`seller-${k}`} {...props} />
              {errors[k] && <div className="bf-err" data-testid={`seller-${k}-error`}>{errors[k]}</div>}
            </div>
          ))}
          <div className="bf-actions" style={{ gridColumn: "1 / -1" }}>
            <button className="btn primary" disabled={busy} data-testid="seller-save">{busy ? "Gemmer…" : "Gem fakturaindstillinger"}</button>
          </div>
        </form>
      )}
      {open && <SmsBillingSettings />}
      {open && <MailSettings />}
    </section>
  );
}
